import {
  incursPenalty,
  isFuture,
  isNow,
  isStartWithinLeadTime,
} from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-dates';
import type {
  CoachingConfig,
  CoachingSession,
  CoachProfile,
} from '@efdigital/ll-addon-data';
import type { CoachingPlanner } from '@efdigital/ll-xceed-v2/utils/coaching/coachee/coaching-plan-manager';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { cell, resource, use } from 'ember-resources';

import { CancelSessionModal } from './session-action-modals/cancel-session.gts';
import { ConfirmAttendanceModal } from './session-action-modals/confirm-attendance.gts';

interface SessionActionsSignature {
  Args: {
    planner: CoachingPlanner;
    session: CoachingSession;
    coach: CoachProfile;
    coachingConfig: CoachingConfig;
    buttonClass?: string;
  };
  Element: HTMLDivElement;
}

function not(condition: boolean): boolean {
  return !condition;
}

type CurrentStatus = {
  canJoin: boolean;
  canReschedule: boolean;
  canCancel: boolean;
  isScheduled: boolean;
  incursPenalty: boolean;
};

function statusEquals(status1: CurrentStatus, status2: CurrentStatus): boolean {
  return (
    status1.canJoin === status2.canJoin &&
    status1.canReschedule === status2.canReschedule &&
    status1.canCancel === status2.canCancel &&
    status1.isScheduled === status2.isScheduled &&
    status1.incursPenalty === status2.incursPenalty
  );
}

export class SessionActions extends Component<SessionActionsSignature> {
  @tracked loading = false;

  get canMarkAttendance(): boolean {
    return (
      this.args.session.status === 'completed' &&
      !this.args.session.coacheeReport
    );
  }

  calculateStatus() {
    return {
      canJoin:
        isStartWithinLeadTime(this.args.session) || isNow(this.args.session),
      canReschedule:
        isFuture(this.args.session) && this.args.session.status === 'scheduled',
      canCancel:
        isFuture(this.args.session) && this.args.session.status === 'scheduled',
      isScheduled: this.args.session.status === 'scheduled',
      incursPenalty: incursPenalty(this.args.session, this.args.coachingConfig),
    };
  }

  @use currentStatus = resource<CurrentStatus>(({ on }) => {
    const status = cell(this.calculateStatus());

    const timer = setInterval(() => {
      const newStatus = this.calculateStatus();
      if (!statusEquals(status.current, newStatus)) {
        status.set(newStatus);
      }
    }, 60 * 1000);

    on.cleanup(() => clearInterval(timer));

    return () => status.current;
  });

  <template>
    <div ...attributes>
      {{#if this.currentStatus.isScheduled}}
        <button
          class="btn btn-primary {{@buttonClass}}"
          type="button"
          disabled={{not this.currentStatus.canJoin}}
        ><iconify-icon inline icon="ph:video-camera" aria-hidden="true" />
          Join</button>
      {{/if}}

      {{!--  TODO: Rescheduling is sugar for cancel + book;
      {{#if this.currentStatus.canReschedule}}
        {{#if (incursPenalty @session @coachingConfig)}}
          <button
            class="btn btn-outline-danger {{@buttonClass}}"
            type="button"
          ><iconify-icon inline icon="ph:warning-circle" aria-hidden="true" />
            Reschedule</button>
        {{else}}
          <button
            class="btn btn-outline-primary {{@buttonClass}}"
            type="button"
          ><iconify-icon inline icon="ph:clock" aria-hidden="true" />
            Reschedule</button>
        {{/if}}
      {{/if}}
      --}}

      {{#if this.currentStatus.canCancel}}
        <CancelSessionModal
          @session={{@session}}
          @planner={{@planner}}
          @incursPenalty={{this.currentStatus.incursPenalty}}
          as |showModal|
        >
          {{#if this.currentStatus.incursPenalty}}
            <button
              class="btn btn-outline-danger {{@buttonClass}}"
              type="button"
              {{on "click" showModal}}
            ><iconify-icon inline icon="ph:warning-circle" aria-hidden="true" />
              Cancel</button>
          {{else}}
            <button
              class="btn btn-outline-primary {{@buttonClass}}"
              type="button"
              {{on "click" showModal}}
            ><iconify-icon inline icon="ph:x" aria-hidden="true" />
              Cancel</button>
          {{/if}}
        </CancelSessionModal>
      {{/if}}

      {{#if this.canMarkAttendance}}
        <ConfirmAttendanceModal
          @session={{@session}}
          @planner={{@planner}}
          as |showModal|
        >
          <button
            class="btn btn-outline-primary {{@buttonClass}}"
            type="button"
            {{on "click" showModal}}
          >Report session outcome
            <iconify-icon inline icon="ph:clipboard-text" aria-hidden="true" />
          </button>
        </ConfirmAttendanceModal>
      {{/if}}
    </div>
  </template>
}
